
import { useEffect, useRef, useState } from "react";
import { Crown, Users, Wallet, ShieldCheck, Settings2, TrendingUp } from "lucide-react";
const BRAND_GREEN = "#33F7BE";
const FEATURES = [
    {
        icon: Crown,
        title: "Your brand, not theirs",
        copy: "Renters book under your name and experience your brand, not a marketplace's.",
    },
    {
        icon: Users,
        title: "Direct relationships",
        copy: "Own every renter relationship and their contact details from day one.",
    },
    {
        icon: Wallet,
        title: "Accept all payments",
        copy: "Control how you get paid, your payout terms, and your own pricing.",
    },
    {
        icon: ShieldCheck,
        title: "Insurance strategy",
        copy: "Choose embedded On Rent and Off Rent coverages or \"BYOI\" bring your own insurance.",
    },
    {
        icon: Settings2,
        title: "Automated operations",
        copy: "Automated and manual rental workflows customized and built he way you want.",
    },
    {
        icon: TrendingUp,
        title: "Scale on your terms",
        copy: "Grow your fleet with personal, leisure, gig-economy, rideshare rental revenue channels.",
    },
];
function revealStyle(shown, delay) {
    return {
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(18px)",
        transition: "opacity 0.8s cubic-bezier(0.22,1,0.36,1), transform 0.8s cubic-bezier(0.22,1,0.36,1)",
        transitionDelay: `${delay}ms`,
    };
}
function useReveal() {
    const ref = useRef(null);
    const [shown, setShown] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        if (typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            setShown(true);
            return;
        }
        const io = new IntersectionObserver((entries) => {
            for (const e of entries) {
                if (e.isIntersecting) {
                    setShown(true);
                    io.disconnect();
                    break;
                }
            }
        }, { threshold: 0.2, rootMargin: "0px 0px -10% 0px" });
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return { ref, shown };
}
const TURO_CONTENT = {
    eyebrow: "A real Turo alternative",
    heading: (<>
      You&apos;re in
      <br />
      full control
    </>),
    lead: "Marketplaces help you rent on their platform.",
    leadBold: "Rentid helps you build your own business.",
    body: "You're in control to own, grow, and scale with your brand, your renters, your payments, your insurance strategy, and your operations all working around you.",
    tagline: "Your fleet. Your business. Your infrastructure.",
    features: FEATURES,
};
export function TuroAltIntroCopy({ content = TURO_CONTENT }) {
    const { ref, shown } = useReveal();
    const { ref: featuresRef, shown: featuresShown } = useReveal();
    return (<div ref={ref}>
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
        {/* Left — eyebrow + big heading */}
        <div className="lg:pt-2">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-5" style={revealStyle(shown, 0)}>
            {content.eyebrow}
          </p>
          <h2 className={content.headingClassName ??
            "font-heading text-6xl sm:text-7xl lg:text-[72px] font-bold tracking-tighter leading-[0.9] text-foreground"} style={revealStyle(shown, 150)}>
            {content.heading}
          </h2>
          {content.headingCta ? (<div className="mt-8" style={revealStyle(shown, 300)}>
              {content.headingCta}
            </div>) : null}
        </div>

        {/* Right — headline, subcopy */}
        <div className="lg:pt-2 lg:pl-12 xl:pl-20">
          {/* Invisible spacer mirrors the left eyebrow so the copy aligns with the headline, not the eyebrow */}
          <p aria-hidden="true" className="invisible hidden lg:block text-xs font-semibold uppercase tracking-widest mb-5">
            {content.eyebrow}
          </p>
          <h3 className="font-heading text-xl sm:text-2xl font-normal tracking-tight leading-snug text-foreground text-balance">
            <span className="block" style={revealStyle(shown, 300)}>
              {content.lead}
            </span>
            <span className="shimmer-text mt-1 block font-bold" style={revealStyle(shown, 550)}>
              {content.leadBold}
            </span>
          </h3>

          <p className={`mt-4 text-sm text-muted-foreground leading-relaxed text-pretty max-w-xl ${content.bodyClassName ?? ""}`} style={revealStyle(shown, 800)}>
            {content.body}
            <br />
            {content.tagline ? (<span className="mt-3 inline-block font-semibold text-foreground">{content.tagline}</span>) : null}
          </p>
        </div>
      </div>

      {/* Feature grid — full width, 3 columns x 2 rows, revealed as it scrolls into view */}
      <ul ref={featuresRef} className="mt-16 grid grid-cols-1 gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
        {content.features.map((f, i) => {
            const Icon = f.icon;
            return (<li key={f.title} style={revealStyle(featuresShown, i * 110)}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#141414]">
                <Icon className="h-5 w-5" strokeWidth={2} style={{ color: BRAND_GREEN }}/>
              </span>
              <p className="mt-4 text-base font-bold tracking-tight text-foreground">{f.title}</p>
              <p className="mt-2 max-w-xs text-sm leading-relaxed text-muted-foreground text-pretty">{f.copy}</p>
            </li>);
        })}
      </ul>
    </div>);
}
